import { computed, type MaybeRefOrGetter, toValue, watch } from "vue"
import { useQuery } from "@vue/apollo-composable"
import { enrichField } from "@auger/core/collections"
import type {
  RecordFieldsFragment,
  RecordLinkFlatFieldsFragment,
  RequiredRecordFieldsFragment,
} from "@auger/core/gql/types"
import { titleCase } from "@auger/ui/utils/string"
import { fetchLinkedTasks, fetchRecordsByType } from "@/common/hasura/records"
import { useLinkedRecords } from "@/composables/useLinkedRecords"
import { getUserRecordId, useUserStore } from "@/stores/user"
import { errorMessage } from "@/utils/errors"
import type { ComposableDeps, Toast } from "./types"

// Statuses that indicate a task is complete
const COMPLETED_STATUSES = ["done", "dismissed"]

/**
 * One of a task's links, as far as the helpers below read it. Tasks arrive from
 * several queries (the linked-tasks query, the task page, the board's
 * useLinkedRecords) and every one's links carry these fields; `linked` is null
 * on a board link whose far end didn't resolve.
 */
type TaskLink = Pick<RecordLinkFlatFieldsFragment, "schema_key" | "linked_id"> & {
  linked?: RequiredRecordFieldsFragment | null
}
type TaskLinks = { links?: readonly TaskLink[] | null }
type TaskData = Partial<Pick<RecordFieldsFragment, "data">>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null

/** A row's data bag, when it has one — for callers holding a collection item as unknown. */
export function taskData(task: unknown): Record<string, unknown> | undefined {
  if (!isRecord(task)) return undefined
  return isRecord(task.data) ? task.data : undefined
}

/** A task's status, when its data holds a string one. */
function taskStatus(task: unknown): string | undefined {
  const status = taskData(task)?.status
  return typeof status === "string" ? status : undefined
}

/**
 * Pure function to compute the data changes when a task's status changes.
 * Only touches completed_at when the completion state actually changes:
 * - Transitioning to done/dismissed: sets completed_at to now
 * - Transitioning from done/dismissed to todo/doing: clears completed_at
 * - Same completion state (e.g. done→dismissed): preserves existing completed_at
 */
export function updateTaskStatus(record: TaskData | null | undefined, newStatus: unknown) {
  const wasCompleted = isCompletedStatus(taskStatus(record))
  const willBeCompleted = isCompletedStatus(newStatus)

  const changes: { status: unknown; completed_at?: string | null } = { status: newStatus }

  if (!wasCompleted && willBeCompleted) {
    changes.completed_at = new Date().toISOString()
  } else if (wasCompleted && !willBeCompleted) {
    changes.completed_at = null
  }
  // Between two completed states (done→dismissed): preserve completed_at
  // Between two non-completed states (todo→doing): no change needed

  return changes
}

/** True when the given status counts as "completed" (done or dismissed). */
export function isCompletedStatus(status: unknown) {
  return typeof status === "string" && COMPLETED_STATUSES.includes(status)
}

/** Whether a task is past its due date and not completed. */
export function isTaskOverdue(task: TaskData | null | undefined) {
  const due = task?.data?.due_date
  if (typeof due !== "string" || !due) return false
  if (isCompletedStatus(taskStatus(task))) return false
  return new Date(due) < new Date(new Date().toDateString())
}

const DAY_MS = 86_400_000

/**
 * The due date as whole days from today, for an open task: "Due today",
 * "Due tomorrow", "Due in 5 days", "1 day overdue", "12 days overdue".
 * Null for a task with no due date or one that's done/dismissed.
 */
export function taskDueLabel(task: TaskData | null | undefined): string | null {
  const due = task?.data?.due_date
  if (!due || isCompletedStatus(taskStatus(task))) return null
  // Due dates are calendar days stored as UTC midnight; read the date part so a
  // US timezone doesn't shift it to the day before.
  const [y = NaN, m = NaN, d = NaN] = String(due).slice(0, 10).split("-").map(Number)
  const dueDay = new Date(y, m - 1, d).getTime()
  const today = new Date(new Date().toDateString()).getTime()
  if (!Number.isFinite(dueDay)) return null
  const days = Math.round((dueDay - today) / DAY_MS)
  if (days === 0) return "Due today"
  if (days === 1) return "Due tomorrow"
  if (days > 1) return `Due in ${days} days`
  return `${-days} ${days === -1 ? "day" : "days"} overdue`
}

/** The due-date buckets a task groups into, in time order. */
export const TASK_DUE_BUCKETS = [
  "Overdue",
  "Earlier",
  "Today",
  "Next 7 days",
  "Next 30 days",
  "Later",
  "No due date",
] as const

/**
 * Which due-date bucket a task falls in, by whole days from today. A past date
 * is "Overdue" for an open task and "Earlier" for a finished one.
 */
export function taskDueBucket(
  task: TaskData | null | undefined
): (typeof TASK_DUE_BUCKETS)[number] {
  const due = task?.data?.due_date
  if (!due) return "No due date"
  const [y = NaN, m = NaN, d = NaN] = String(due).slice(0, 10).split("-").map(Number)
  const dueDay = new Date(y, m - 1, d).getTime()
  if (!Number.isFinite(dueDay)) return "No due date"
  const today = new Date(new Date().toDateString()).getTime()
  const days = Math.round((dueDay - today) / DAY_MS)
  if (days < 0) return isCompletedStatus(taskStatus(task)) ? "Earlier" : "Overdue"
  if (days === 0) return "Today"
  if (days <= 7) return "Next 7 days"
  if (days <= 30) return "Next 30 days"
  return "Later"
}

// Which related record a card names first: the company, then the deal, the
// meeting, the objective, the person; anything else after.
const RELATED_TYPE_ORDER = ["organization", "deal", "event", "objective", "person"]

/**
 * A task's `related_to` records, most important first. Cards show the first
 * and count the rest.
 */
export function relatedRecords(task: TaskLinks | null | undefined): RequiredRecordFieldsFragment[] {
  const rank = (type: string) => {
    const i = RELATED_TYPE_ORDER.indexOf(type)
    return i === -1 ? RELATED_TYPE_ORDER.length : i
  }
  return (task?.links ?? [])
    .flatMap((l) => (l.schema_key === "related_to" && l.linked ? [l.linked] : []))
    .sort((a, b) => rank(a.type) - rank(b.type))
}

/** The task's project record, if it has one. */
export function taskProject(
  task: TaskLinks | null | undefined
): RequiredRecordFieldsFragment | null {
  return (task?.links ?? []).find((l) => l.schema_key === "project" && l.linked)?.linked ?? null
}

/**
 * Resolve all person record ids assigned to a task (equal co-assignment).
 * Reads every `assigned_to` link; ignores links with a null target.
 */
export function getTaskAssigneeIds(task: TaskLinks | null | undefined): number[] {
  return (task?.links ?? [])
    .filter((l) => l.schema_key === "assigned_to" && l.linked_id != null)
    .map((l) => Number(l.linked_id))
}

/**
 * Display names for a task's assignees — one entry per `assigned_to` link, in
 * link order, so the count matches `getTaskAssigneeIds`.
 *
 * The name comes off the link's own `linked` record wherever the query fetched
 * it (`RecordLinkBrowserFields`), which names any assigned person, not only
 * those who happen to be Auger users. `resolveName` is the fallback for callers
 * whose links carry only an id — it's how the home-page task list keeps
 * resolving through the users store.
 *
 * An assignee neither route can name reads "Unknown user" rather than being
 * dropped, so an assigned task is never mistaken for an unassigned one.
 */
export function getTaskAssigneeNames(
  task: TaskLinks | null | undefined,
  resolveName?: (personId: number) => string | null | undefined
): string[] {
  return (task?.links ?? [])
    .filter((l) => l.schema_key === "assigned_to" && l.linked_id != null)
    .map((l) => l.linked?.name || resolveName?.(Number(l.linked_id)) || "Unknown user")
}

/**
 * Pure edit-permission rule. Admins edit anything; unassigned tasks are
 * editable by anyone; otherwise the user must be one of the assignees.
 */
export function canUserEditTask({
  isAdmin,
  userRecordId,
  assigneeIds,
}: {
  isAdmin: boolean
  userRecordId: number | null
  assigneeIds: number[]
}): boolean {
  if (isAdmin) return true
  if (assigneeIds.length === 0) return true
  return userRecordId != null && assigneeIds.includes(userRecordId)
}

/**
 * Check if the current user can edit a task. Reads the user store, then
 * delegates to the pure `canUserEditTask` rule.
 */
export function canEditTask(task: TaskLinks | null | undefined) {
  const userStore = useUserStore()
  return canUserEditTask({
    isAdmin: userStore.isAdmin ?? false,
    userRecordId: getUserRecordId(),
    assigneeIds: getTaskAssigneeIds(task),
  })
}

/**
 * Curated display order for status groups — in-progress first, so active work
 * surfaces at the top of a task list.
 */
export const TASK_STATUS_ORDER = ["doing", "todo", "done", "dismissed"]

/**
 * The field descriptor every task list drives its status control from. Resolved
 * through the field registry (task_status → StatusSelectField) rather than
 * hand-rolled per block.
 */
export const taskStatusField = enrichField({
  key: "status",
  type: "task_status",
  label: "Status",
  isEditable: true,
})

/**
 * Group tasks by status for a RecordListBlock, sorted by due date within each
 * group (tasks with no due date trail). Empty groups are dropped. `filter` is
 * "active" (hides done/dismissed), "all", or a single status.
 */
export function groupTasksByStatus<T extends TaskData>(
  tasks: readonly T[] | null | undefined,
  filter = "active"
) {
  const visibleStatuses =
    filter === "all" ? TASK_STATUS_ORDER : filter === "active" ? ["doing", "todo"] : [filter]

  return visibleStatuses
    .map((status) => ({
      key: status,
      label: titleCase(status),
      items: (tasks || [])
        .filter((t) => (taskStatus(t) || "todo") === status)
        .sort((a, b) => {
          const da = a?.data?.due_date || "9999"
          const db = b?.data?.due_date || "9999"
          return String(da).localeCompare(String(db))
        }),
    }))
    .filter((g) => g.items.length > 0)
}

const MY_TASK_SECTIONS: Array<{
  key: string
  label: string
  bucket?: (typeof TASK_DUE_BUCKETS)[number]
  tone?: "danger" | "link"
  collapsed?: boolean
}> = [
  { key: "doing", label: "In progress", tone: "link" },
  { key: "overdue", label: "Overdue", bucket: "Overdue", tone: "danger" },
  { key: "today", label: "Today", bucket: "Today" },
  { key: "week", label: "Next 7 days", bucket: "Next 7 days" },
  { key: "month", label: "Next 30 days", bucket: "Next 30 days" },
  { key: "later", label: "Later", bucket: "Later" },
  { key: "none", label: "No due date", bucket: "No due date", collapsed: true },
]

/**
 * Someone's open tasks for the My Tasks block, by when they need doing: in
 * progress first, then the due-date buckets (the board's "Group: Due date"),
 * soonest first within each. "No due date" — where most agent-made tasks sit —
 * starts folded. Empty sections are dropped; every section can fold.
 */
export function groupMyTasks<T extends TaskData & Partial<Pick<RecordFieldsFragment, "name">>>(
  tasks: readonly T[] | null | undefined
) {
  const open = (tasks || []).filter((t) => !isCompletedStatus(taskStatus(t)))
  const byDue = (a: T, b: T) =>
    String(a?.data?.due_date || "9999").localeCompare(String(b?.data?.due_date || "9999")) ||
    String(a?.name || "").localeCompare(String(b?.name || ""))
  return MY_TASK_SECTIONS.map(({ key, label, bucket, tone, collapsed }) => ({
    key,
    label,
    tone,
    collapsible: true,
    collapsed: !!collapsed,
    items: open
      .filter((t) =>
        bucket
          ? taskStatus(t) !== "doing" && taskDueBucket(t) === bucket
          : taskStatus(t) === "doing"
      )
      .sort(byDue),
  })).filter((g) => g.items.length > 0)
}

/**
 * The record ids a record's related tasks are scoped to: the record itself plus
 * anything it links to via `organization`. A deal's tasks are often filed
 * against its company rather than the deal, so both surface on the deal page;
 * for record types with no organization link this is just `[record.id]`. The
 * ids are de-duped, the record's own first.
 */
export function taskScopeIds(
  record: (Partial<Pick<RecordFieldsFragment, "id">> & TaskLinks) | null | undefined
): number[] {
  if (!record?.id) return []
  const orgIds = (record.links || [])
    .filter((l) => l.schema_key === "organization" && l.linked_id != null)
    .map((l) => Number(l.linked_id))
  return Array.from(new Set([record.id, ...orgIds]))
}

/** The link keys a task can be found by: what it's about, and who owns it. */
export type TaskLinkKey = "related_to" | "assigned_to"

/**
 * Tasks linked to a record by one key — `related_to` for the work arising from
 * it (widened to its organization, see `taskScopeIds`), `assigned_to` for the
 * work a person owns.
 *
 * Shares Apollo's cache with every other instance; writers fan out via
 * `client.refetchQueries({ include: ["FetchLinkedTasks"] })`.
 *
 */
export function useLinkedTasks(
  recordRef: MaybeRefOrGetter<Parameters<typeof taskScopeIds>[0]>,
  linkKey: MaybeRefOrGetter<TaskLinkKey> = "related_to",
  { toast }: { toast?: Toast | null } = {}
) {
  const getRecord = () => toValue(recordRef)
  // Only `related_to` widens to the organization: a task assigned to a person
  // is theirs alone, never their company's.
  const scopeIds = computed(() =>
    toValue(linkKey) === "related_to"
      ? taskScopeIds(getRecord())
      : [getRecord()?.id].filter((id): id is number => !!id)
  )

  const { result, loading, error, refetch } = useQuery(
    fetchLinkedTasks,
    () => ({ ids: scopeIds.value, linkKey: toValue(linkKey) }),
    () => ({
      enabled: scopeIds.value.length > 0,
      fetchPolicy: "cache-and-network",
      errorPolicy: "all",
      keepPreviousResult: true,
    })
  )

  watch(error, (e) => {
    if (!e) return
    console.error("useLinkedTasks query error:", e)
    const message = errorMessage(e)
    toast?.error?.(`Failed to load tasks: ${message}`)
  })

  // Hasura's relationship filter is an EXISTS subquery, so a task matching
  // several scope ids still comes back once.
  const tasks = computed(() => result.value?.tasks ?? [])
  const loaded = computed(() => !!result.value)

  return { tasks, loaded, loading, error, refetch }
}

/**
 * Every task, as a collection DATA composable (the useLinkedRecords shape) for
 * the Tasks board and list. It layers the task rules useTask applies to a
 * single task onto the collection, so a drag, an inline edit and a bulk edit
 * all behave like the task page:
 *  - a `status` write also stamps/clears data.completed_at (updateTaskStatus),
 *    in one write so the two keys can't race;
 *  - only an assignee edits a task (admins, and anyone on an unassigned task,
 *    too — canEditTask);
 *  - a task with no status reads as `todo`, as the task blocks treat it.
 * The task blocks apply the same two write rules to their own status writes
 * (setRecordStatus in blocks/summary/status.ts, an optimistic Apollo cache
 * write). Duplicated for now; keep the paths in step.
 */
export function useTaskRecords(deps: ComposableDeps) {
  const records = useLinkedRecords(
    { query: fetchRecordsByType, variables: { type: "task" }, recordType: "task" },
    deps
  )

  const readStatus = (item: unknown) => taskStatus(item) || "todo"
  const fields = computed(() =>
    records.fields.value.map((f) =>
      f.key === "status" ? { ...f, value: readStatus, rawValue: readStatus } : f
    )
  )

  const findTask = (id: number | string) => records.items.value.find((r) => r.id === Number(id))

  function updateItem(id: number, fieldKey: string, value: unknown) {
    const task = findTask(id)
    if (task && !canEditTask(task)) {
      deps.toast?.error?.("Only an assignee can edit this task")
      return Promise.resolve(false)
    }
    if (task && fieldKey === "status") {
      return records.updateItemFields(task.id, updateTaskStatus(task, value))
    }
    return records.updateItem(id, fieldKey, value)
  }

  return { ...records, fields, updateItem }
}
